import { Eye, HelpCircle, ListChecks, ShieldAlert, Compass, RefreshCw } from "lucide-react";

export const SECTIONS = [
  { key: "view", label: "EV-IDEX View", icon: Eye, tone: "cyan", rx: /^(ev[- ]?idex(?:'s)? view|what ev[- ]?idex sees|ev[- ]?idex sees|summary|the view)$/i },
  { key: "why", label: "Why", icon: HelpCircle, tone: "sky", rx: /^(why|why (?:it|this) matters|why it matters to you|reasoning)$/i },
  { key: "evidence", label: "Evidence", icon: ListChecks, tone: "indigo", rx: /^(evidence|the evidence|evidence for|verified facts|data|key numbers)$/i },
  { key: "risk", label: "Risk", icon: ShieldAlert, tone: "amber", rx: /^(risks?|the risks?|risk factors|what could go wrong)$/i },
  { key: "choices", label: "Your Choices", icon: Compass, tone: "emerald", rx: /^(your (?:choices|options)|choices|options|next steps|what you can do)$/i },
  { key: "change", label: "What Would Change the Decision", icon: RefreshCw, tone: "rose", rx: /^(what would change (?:the|this) (?:decision|analysis|view)|what (?:would|could) change (?:it|this)|what changes (?:it|this|the analysis)|invalidation|what would change)$/i },
];

const TONE = {
  cyan: "border-cyan-500/40 bg-cyan-500/[0.07] text-cyan-200",
  sky: "border-sky-500/40 bg-sky-500/[0.07] text-sky-200",
  indigo: "border-indigo-400/40 bg-indigo-500/[0.07] text-indigo-200",
  amber: "border-amber-500/40 bg-amber-500/[0.07] text-amber-200",
  emerald: "border-emerald-500/40 bg-emerald-500/[0.07] text-emerald-200",
  rose: "border-rose-500/40 bg-rose-500/[0.07] text-rose-200",
};

const HEAD_RX = /^\s*(?:#{1,4}\s*)?(?:\*\*|__)?\s*([^*:_\n]{2,50})\s*(?:\*\*|__)?\s*:?\s*(?:\*\*|__)?\s*(?:[-–—]\s+)?(.*)$/;

const matchSection = (line) => {
  if (!/^\s*(?:#{1,4}\s*)?(?:\*\*|__)?\s*[A-Za-z]/.test(line)) return null;
  const m = line.match(HEAD_RX);
  if (!m) return null;
  const label = m[1].trim();
  const sec = SECTIONS.find((s) => s.rx.test(label));
  if (!sec) return null;
  const rest = m[2].trim();
  const headPart = rest ? line.slice(0, line.lastIndexOf(rest)) : line;
  if (rest && !/[*:#]/.test(headPart)) return null;
  return { sec, rest: rest.replace(/^\*\*|\*\*$/g, "").trim() };
};

// Splits raw assistant text into [{sec|null, lines[]}] blocks.
export const parseAnswer = (text) => {
  const blocks = [{ sec: null, lines: [] }];
  for (const raw of (text || "").split("\n")) {
    const hit = matchSection(raw);
    if (hit) {
      blocks.push({ sec: hit.sec, lines: hit.rest ? [hit.rest] : [] });
    } else {
      blocks[blocks.length - 1].lines.push(raw);
    }
  }
  return blocks.filter((b) => b.sec || b.lines.some((l) => l.trim()));
};

const Inline = ({ text }) => {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i} className="font-semibold text-slate-50">{p.slice(2, -2)}</strong>;
    if (p.startsWith("`") && p.endsWith("`")) return <code key={i} className="font-mono-ticker text-[0.92em] px-1 py-0.5 rounded bg-slate-800/80 text-cyan-100">{p.slice(1, -1)}</code>;
    return <span key={i}>{p}</span>;
  });
};

const Lines = ({ lines }) => {
  const out = [];
  let list = null;
  const flush = () => { if (list) { out.push(list); list = null; } };
  lines.forEach((raw, i) => {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) { flush(); return; }
    const bullet = line.match(/^\s*(?:[-•*]|\d+[.)])\s+(.*)$/);
    if (bullet) {
      const ordered = /^\s*\d/.test(line);
      if (!list || list.ordered !== ordered) { flush(); list = { ordered, items: [], key: `l${i}` }; }
      list.items.push(bullet[1]);
      return;
    }
    flush();
    const h = line.match(/^\s*#{1,4}\s*(.+?)\s*#*$/) || line.match(/^\s*\*\*([^*]+)\*\*\s*:?\s*$/);
    if (h) out.push({ heading: h[1], key: `h${i}` });
    else out.push({ para: line, key: `p${i}` });
  });
  flush();
  return out.map((b) => {
    if (b.items) {
      const Tag = b.ordered ? "ol" : "ul";
      return (
        <Tag key={b.key} className={`${b.ordered ? "list-decimal" : "list-disc"} pl-5 md:pl-6 space-y-1.5 marker:text-cyan-400/80`}>
          {b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}
        </Tag>
      );
    }
    if (b.heading) return <div key={b.key} className="font-display font-semibold text-slate-100 pt-1"><Inline text={b.heading} /></div>;
    return <p key={b.key}><Inline text={b.para} /></p>;
  });
};

// Hide the trailing <<followups>> block (and a partially streamed marker) from the visible answer.
export const visibleAnswer = (text) => {
  const t = text || "";
  const i = t.indexOf("<<");
  if (i < 0) return t;
  const tail = t.slice(i);
  return "<<followups>>".startsWith(tail.slice(0, 13)) || tail.startsWith("<<followups>>") ? t.slice(0, i).trimEnd() : t;
};

export const AnswerBody = ({ text, streaming }) => {
  const blocks = parseAnswer(visibleAnswer(text));
  return (
    <div className="space-y-4" data-testid="answer-body">
      {blocks.map((b, i) => {
        if (!b.sec) return <div key={`f${i}`} className="space-y-3"><Lines lines={b.lines} /></div>;
        const Icon = b.sec.icon;
        return (
          <section key={`${b.sec.key}-${i}`} data-testid={`answer-section-${b.sec.key}`} className={`rounded-xl border-l-[3px] border ${TONE[b.sec.tone]} px-3 py-3 md:px-5 md:py-4`}>
            <div className="flex items-center gap-2 mb-2 font-display font-bold tracking-tight text-[0.95rem] md:text-base">
              <Icon className="w-4 h-4 shrink-0" /> {b.sec.label}
            </div>
            <div className="space-y-3 text-slate-200"><Lines lines={b.lines} /></div>
          </section>
        );
      })}
      {streaming && <span className="inline-block w-2 h-5 align-text-bottom bg-cyan-400 animate-pulse rounded-sm" />}
    </div>
  );
};
